import { useEffect, useState } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { PlusIcon } from '@/shared/ui'
import { postWithAuth } from '@/api/httpRequests'
import BasicModal from '@/shared/ui/Modal'
import PinInputField from '@/shared/ui/PinInputField'
import { usePinVerification } from '@/widgets/ProfileScreen/hooks/usePinVerification'
import { getErrorMessage } from '@/shared/lib'
import { vodPurchaseUrl } from '@/shared/constants'
import { useTranslation } from 'react-i18next'
import { Offer } from '@/types/vod/VodItemDetails'
import {
    getFromLocalStorage,
    removeFromLocalStorage,
    saveToLocalStorage,
} from '@/shared/localStorage/localStorageRepository'

interface Props {
    shouldCheckPin: {
        shouldCheck: boolean
        id: string
        isKid?: boolean | undefined
        isEdit?: boolean | undefined
        isDelete?: boolean | undefined
        isSelect?: boolean | undefined
    }
    isModalOpen: boolean
    setIsModalOpen: (value: boolean) => void
    handleOpenModal: (isPinValid: boolean) => void
    fetchDetails: () => void | undefined
    offers?: Offer[]
    productId?: string
}

const PinModal = ({
    isModalOpen,
    setIsModalOpen,
    handleOpenModal,
    fetchDetails,
    offers,
    productId,
}: Props) => {
    const {
        pin,
        setPin,
        isPinValid,
        isPinError,
        setIsPinError,
        setIsPinValid,
        handleCheckPin,
        shouldCheckPin,
        setShouldCheckPin,
    } = usePinVerification()
    const theme = useTheme()
    const [purchase, setPurchase] = useState({
        status: '',
        message: '',
    })
    const [offer, setOffer] = useState({
        productId: '',
        offerId: '',
    })
    const { t: translate } = useTranslation()

    const handleChangePin = (value: string) => {
        setPin(value)
        setIsPinError('')
    }

    const handleCancelPin = () => {
        setShouldCheckPin({ shouldCheck: false, id: '-1' })
        setPin('')
        setIsPinError('')
        setIsModalOpen(false)
    }

    const fetchContentWithAuth = async (
        url: string,
        body: {
            offerId: string
            productId: string
        }
    ) => {
        try {
            const requestBody = JSON.stringify(body)
            const text = await postWithAuth(url, false, requestBody)

            saveToLocalStorage(
                'purchaseStatus',
                JSON.stringify({
                    offerId: offer.offerId,
                    productId: offer.productId,
                    status: 'ok',
                })
            )
            setPurchase(text)
        } catch (error) {
            getErrorMessage('Error fetching content:', error)
        }
    }

    function convertRentalPeriodToDays(rentalPeriod: string): string {
        const minutes = parseInt(rentalPeriod, 10)
        const days = minutes / (60 * 24)

        return `${Math.round(days * 2) / 2}`
    }

    const handleModalButtons = () => {
        setIsModalOpen(false)
        setShouldCheckPin({
            shouldCheck: false,
            id: '-1',
        })
        isPinValid && fetchDetails()
        setOffer({
            productId: '',
            offerId: '',
        })
        handleOpenModal(isPinValid)
        removeFromLocalStorage('purchaseStatus')
    }

    useEffect(() => {
        setShouldCheckPin((prev) => ({ ...prev, shouldCheck: true }))
    }, [isModalOpen])

    useEffect(() => {
        if (isPinValid && offer.offerId) {
            fetchContentWithAuth(vodPurchaseUrl, offer)
        }
    }, [isPinValid && offer.offerId])

    useEffect(() => {
        const storedPurchase = getFromLocalStorage('purchaseStatus')
        if (storedPurchase) {
            fetchDetails()
            removeFromLocalStorage('purchaseStatus')
        }
    }, [])

    const buttonHover = {
        '&:hover': {
            color: 'black',
            backgroundColor: theme.palette.primary.main,
        },
    }

    return (
        <BasicModal
            styles={{
                maxWidth: '474px',
                width: 'max-content',
            }}
            isOpen={isModalOpen}
            setIsModalOpen={() => handleOpenModal(isPinValid)}>
            <Button
                onClick={() => {
                    handleModalButtons()
                    setIsModalOpen(false)
                }}
                style={{
                    transform: 'rotate(45deg)',
                }}>
                <PlusIcon fill={theme.palette.primary.main} />
            </Button>

            {offer.offerId && shouldCheckPin.shouldCheck && (
                <PinInputField
                    onChangePin={handleChangePin}
                    onCheckPin={() => handleCheckPin(pin, shouldCheckPin.id)}
                    onCancelPin={handleCancelPin}
                    isPinError={!!isPinError}
                />
            )}

            {offers?.length && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginTop: '16px',
                        justifyContent: 'flex-start',
                        width: '100%',
                    }}>
                    {!offer.offerId &&
                        offers.map((offer) => (
                            <Box
                                key={offer.id}
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                sx={{
                                    transition: 'all .2s ease-out;',
                                    borderRadius: theme.shape.borderRadius,
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    padding: '8px',
                                    maxWidth: '216px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        border: `1px solid ${theme.palette.custom.black}`,
                                        backgroundColor: 'custom.black',
                                    },
                                }}>
                                <Typography
                                    align="center"
                                    variant="text3"
                                    color="#fff"
                                    mt="16px"
                                    mb="16px">
                                    {offer.name}
                                </Typography>
                                <Typography
                                    align="center"
                                    variant="text3"
                                    color="#fff">
                                    Dostęp na{' '}
                                    {convertRentalPeriodToDays(
                                        offer.rentalPeriod
                                    )}{' '}
                                    {convertRentalPeriodToDays(
                                        offer.rentalPeriod
                                    ) === '1'
                                        ? translate('day')
                                        : translate('days')}
                                </Typography>
                                <Typography
                                    align="center"
                                    variant="text3"
                                    color="#fff"
                                    mt="16px"
                                    mb="16px">
                                    {offer.description}
                                </Typography>
                                <Button
                                    sx={{ ...buttonHover }}
                                    onClick={() => {
                                        setShouldCheckPin((prev) => ({
                                            ...prev,
                                            shouldCheck: true,
                                        }))
                                        setIsPinValid(false)
                                        setOffer({
                                            productId: productId || '',
                                            offerId: offer.id,
                                        })
                                    }}>
                                    {translate('buyAction')}
                                </Button>
                            </Box>
                        ))}
                </Box>
            )}

            {offer.offerId && isPinValid && (
                <Box
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        minWidth: '328px',
                    }}>
                    {purchase.status !== 'ok' ? (
                        <Typography
                            variant="text1"
                            component="p"
                            width="100%"
                            align="center"
                            color="#fff"
                            mt="16px"
                            mb="16px">
                            {purchase.message}
                        </Typography>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center">
                            <Typography
                                align="center"
                                variant="text1"
                                color="#fff"
                                mt="16px"
                                mb="16px">
                                {translate('purchaseOK')}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleModalButtons}>
                                {translate('close')}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </BasicModal>
    )
}

export default PinModal
